import React, { useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { dataGridConfig, columnConfigs } from "../../styles/DataGridTheme";
import "styles/Dashboard.css";
import StyledDataGrid from "../../components/StyledDataGrid";
import PageHeader from "../../components/PageHeader";

const maroon = "#500000";

// Outstanding Reviews Component
const OutstandingReviewsCard = ({ reviews, onReviewPatient }) => {
  const columns = [
    { ...columnConfigs.patientName, flex: 1 },
    { ...columnConfigs.dateOfBirth, flex: 0.8 },
    { ...columnConfigs.studyId, flex: 0.8 },
    {
      ...columnConfigs.actionWide,
      flex: 1.2,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => onReviewPatient(params.row)}
          sx={{
            backgroundColor: maroon,
            height: "36px",
            fontSize: "13px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            "&:hover": { backgroundColor: "#4a0000" },
          }}
        >
          REVIEW PATIENT CONSENT FORM
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ mb: 4, display: "flex", flexDirection: "column" }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: maroon,
          mb: 2,
        }}
      >
        Outstanding Review(s)
      </Typography>

      <Box sx={{ height: 400, width: "100%" }}>
        <StyledDataGrid rows={reviews} columns={columns} {...dataGridConfig} />
      </Box>
    </Box>
  );
};

// Outstanding Surveys Component
const OutstandingSurveysCard = ({ surveys, onSendSurvey }) => {
  const columns = [
    { ...columnConfigs.patientName, flex: 1 },
    { ...columnConfigs.surveyType, flex: 0.8 },
    { ...columnConfigs.studyId, flex: 0.8 },
    {
      ...columnConfigs.action,
      flex: 0.8,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => onSendSurvey(params.row)}
          sx={{
            backgroundColor: maroon,
            height: "36px",
            fontSize: "13px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            "&:hover": { backgroundColor: "#4a0000" },
          }}
        >
          SEND SURVEY
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ mb: 4, display: "flex", flexDirection: "column" }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: maroon,
          mb: 2,
        }}
      >
        Outstanding Survey(s)
      </Typography>

      <Box sx={{ height: 400, width: "100%" }}>
        <StyledDataGrid rows={surveys} columns={columns} {...dataGridConfig} />
      </Box>
    </Box>
  );
};

// Outstanding Todos Component
const OutstandingTodosCard = ({ todos, onSendSurvey }) => {
  const columns = [
    { ...columnConfigs.patientName, flex: 1 },
    { ...columnConfigs.surveyType, flex: 0.8 },
    { ...columnConfigs.studyId, flex: 0.8 },
    {
      ...columnConfigs.action,
      flex: 0.8,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => onSendSurvey(params.row)}
          sx={{
            backgroundColor: maroon,
            height: "36px",
            fontSize: "13px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            "&:hover": { backgroundColor: "#4a0000" },
          }}
        >
          SEND SURVEY
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ mb: 4, display: "flex", flexDirection: "column" }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: maroon,
          mb: 2,
        }}
      >
        Outstanding To-Do(s)
      </Typography>

      <Box sx={{ height: 400, width: "100%" }}>
        <StyledDataGrid rows={todos} columns={columns} {...dataGridConfig} />
      </Box>
    </Box>
  );
};

const ReviewsApprovalsDashboard = () => {
  const [actionMessage, setActionMessage] = useState("");

  // Static placeholder data - no API calls
  const reviews = [
    {
      id: 1,
      patientName: "Default Patient A",
      dateOfBirth: "01/01/1990",
      studyId: "2023-0001",
    },
    {
      id: 2,
      patientName: "Default Patient B",
      dateOfBirth: "02/15/1985",
      studyId: "2023-0001",
    },
    {
      id: 3,
      patientName: "Default Patient C",
      dateOfBirth: "03/20/1992",
      studyId: "2024-0002",
    },
  ];

  const surveys = [
    {
      id: 1,
      patientName: "Default Patient A",
      surveyType: "Satisfaction",
      studyId: "2023-0001",
    },
    {
      id: 2,
      patientName: "Default Patient B",
      surveyType: "Health",
      studyId: "2023-0001",
    },
  ];

  const todos = [
    {
      id: 1,
      patientName: "Default Patient A",
      surveyType: "Satisfaction",
      studyId: "2023-0001",
    },
    {
      id: 2,
      patientName: "Default Patient B",
      surveyType: "Health",
      studyId: "2023-0001",
    },
  ];

  const handleReviewPatient = (review) => {
    console.log("Review patient consent form:", review);
    setActionMessage(
      `Patient consent review for ${review.patientName} is not yet implemented.`
    );
  };

  const handleSendSurvey = (survey) => {
    console.log("Send survey:", survey);
    setActionMessage(
      `Survey sending for ${survey.patientName} is not yet implemented.`
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {actionMessage && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionMessage("")}>
          {actionMessage}
        </Alert>
      )}
      {/* Header */}
      <PageHeader title="Reviews & Approvals" />

      {/* Subtitle */}
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 3, fontStyle: "italic" }}
      >
        Note: This page displays placeholder data. Backend functionality has been removed.
      </Typography>

      {/* Dashboard Cards */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <OutstandingReviewsCard
          reviews={reviews}
          onReviewPatient={handleReviewPatient}
        />
        <OutstandingSurveysCard
          surveys={surveys}
          onSendSurvey={handleSendSurvey}
        />
        <OutstandingTodosCard todos={todos} onSendSurvey={handleSendSurvey} />
      </Box>
    </Box>
  );
};

export default ReviewsApprovalsDashboard;
